import { useEffect, useMemo, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { HelpTip } from "@/components/HelpTip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CreditCard, Loader2, Plus, Pencil, Star, Power, Save, X, BarChart3 } from "lucide-react";

const INSTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const inp = "h-9 bg-zinc-900 border-zinc-800 text-right text-sm";

const empty = () => ({
  id: null, name: "", debito: "", pix: "", credito: Object.fromEntries(INSTS.map((n) => [n, ""])),
  settle_debito: "1", settle_pix: "0", settle_credito: "30",
  split_credit_monthly: true, advance_enabled: false, advance_rate: "", advance_settle: "2",
});

const fromApi = (m) => ({
  id: m.id, name: m.name || "",
  debito: m.debito ? String(m.debito) : "", pix: m.pix ? String(m.pix) : "",
  credito: Object.fromEntries(INSTS.map((n) => [n, m.credito?.[n] ? String(m.credito[n]) : ""])),
  settle_debito: String(m.settle_debito ?? 1), settle_pix: String(m.settle_pix ?? 0),
  settle_credito: String(m.settle_credito ?? 30),
  split_credit_monthly: m.split_credit_monthly !== false,
  advance_enabled: !!m.advance_enabled,
  advance_rate: m.advance_rate ? String(m.advance_rate) : "",
  advance_settle: String(m.advance_settle ?? 2),
});

const toPayload = (f) => ({
  name: f.name.trim() || "Maquininha",
  debito: Number(f.debito) || 0, pix: Number(f.pix) || 0,
  credito: Object.fromEntries(INSTS.map((n) => [String(n), Number(f.credito[n]) || 0]).filter(([, v]) => v > 0)),
  settle_debito: Number(f.settle_debito) || 0, settle_pix: Number(f.settle_pix) || 0,
  settle_credito: Number(f.settle_credito) || 0,
  split_credit_monthly: !!f.split_credit_monthly,
  advance_enabled: !!f.advance_enabled,
  advance_rate: Number(f.advance_rate) || 0,
  advance_settle: Number(f.advance_settle) || 0,
});

function MachineForm({ initial, onCancel, onSaved }) {
  const [f, setF] = useState(() => initial ? fromApi(initial) : empty());
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setC = (n, v) => setF((p) => ({ ...p, credito: { ...p.credito, [n]: v.replace(",", ".") } }));
  const save = async () => {
    setBusy(true);
    try {
      if (initial?.id) await api.patch(`/shop-settings/payment-machines/${initial.id}`, toPayload(f));
      else await api.post("/shop-settings/payment-machines", toPayload(f));
      toast.success(initial ? "Maquininha atualizada" : "Maquininha cadastrada");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`machine-form-${initial?.id || "new"}`} className="rounded-xl border border-sky-800/50 bg-sky-950/10 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Input data-testid="machine-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex.: Stone" className="h-9 bg-zinc-900 border-zinc-800" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs font-mono-caps text-zinc-400">Débito (%)<Input data-testid="machine-debito" inputMode="decimal" value={f.debito} onChange={(e) => set("debito", e.target.value.replace(",", "."))} className={`${inp} w-full mt-1`} /></label>
        <label className="text-xs font-mono-caps text-zinc-400">Pix (%)<Input data-testid="machine-pix" inputMode="decimal" value={f.pix} onChange={(e) => set("pix", e.target.value.replace(",", "."))} className={`${inp} w-full mt-1`} /></label>
      </div>
      <div>
        <div className="text-xs font-mono-caps text-zinc-400 mb-1">Crédito por nº de parcelas (%)</div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {INSTS.map((n) => (
            <label key={n} className="text-[11px] text-zinc-500">{n}x
              <Input data-testid={`machine-credito-${n}`} inputMode="decimal" value={f.credito[n]} onChange={(e) => setC(n, e.target.value)} className={`${inp} w-full mt-0.5`} />
            </label>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 pt-2 border-t border-zinc-800">
        <label className="text-xs font-mono-caps text-zinc-400">Débito D+<Input data-testid="machine-settle-debito" inputMode="numeric" value={f.settle_debito} onChange={(e) => set("settle_debito", e.target.value)} className={`${inp} w-full mt-1`} /></label>
        <label className="text-xs font-mono-caps text-zinc-400">Pix D+<Input data-testid="machine-settle-pix" inputMode="numeric" value={f.settle_pix} onChange={(e) => set("settle_pix", e.target.value)} className={`${inp} w-full mt-1`} /></label>
        <label className="text-xs font-mono-caps text-zinc-400">Crédito D+<Input data-testid="machine-settle-credito" inputMode="numeric" value={f.settle_credito} onChange={(e) => set("settle_credito", e.target.value)} className={`${inp} w-full mt-1`} /></label>
      </div>
      <label className="flex items-center justify-between rounded-lg border border-zinc-800 px-3 py-2 text-xs text-zinc-300">
        <span>Crédito parcelado cai mês a mês <span className="text-zinc-500">(desligue se o banco paga tudo numa data só)</span></span>
        <Switch data-testid="machine-split" checked={f.split_credit_monthly} onCheckedChange={(v) => set("split_credit_monthly", v)} />
      </label>
      <div className="rounded-lg border border-amber-900/50 bg-amber-950/10 p-3 space-y-3">
        <label className="flex items-center justify-between text-xs text-amber-200">
          <span>Antecipo os recebíveis (crédito)</span>
          <Switch data-testid="machine-advance" checked={f.advance_enabled} onCheckedChange={(v) => set("advance_enabled", v)} />
        </label>
        {f.advance_enabled && (
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs font-mono-caps text-zinc-400">Taxa de antecipação (%)<Input data-testid="machine-advance-rate" inputMode="decimal" value={f.advance_rate} onChange={(e) => set("advance_rate", e.target.value.replace(",", "."))} className={`${inp} w-full mt-1`} /></label>
            <label className="text-xs font-mono-caps text-zinc-400">Crédito antecipado D+<Input data-testid="machine-advance-settle" inputMode="numeric" value={f.advance_settle} onChange={(e) => set("advance_settle", e.target.value)} className={`${inp} w-full mt-1`} /></label>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <Button data-testid="machine-cancel" variant="outline" onClick={onCancel} className="bg-zinc-900 border-zinc-800"><X className="h-4 w-4 mr-1" /> Cancelar</Button>
        <Button data-testid="machine-save" onClick={save} disabled={busy || !f.name.trim()} className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Salvar</>}
        </Button>
      </div>
    </div>
  );
}

function MachineRow({ m, onEdit, onReload }) {
  const [busy, setBusy] = useState(false);
  const act = async (p) => { setBusy(true); try { await p; onReload(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const parcelas = Object.entries(m.credito || {}).sort((a, b) => Number(a[0]) - Number(b[0])).map(([k, v]) => `${k}x ${v}%`).join(" · ") || "—";
  return (
    <li data-testid={`machine-row-${m.id}`} className={`rounded-xl border p-3 ${m.active ? "border-zinc-800 bg-zinc-950/60" : "border-zinc-800 bg-zinc-950/30 opacity-60"}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-zinc-100">{m.name}</span>
            {m.is_default && <span data-testid={`machine-default-badge-${m.id}`} className="text-[10px] px-2 py-0.5 rounded-full border border-amber-700 bg-amber-950/40 text-amber-300 uppercase font-bold">Padrão</span>}
            {!m.active && <span className="text-[10px] px-2 py-0.5 rounded-full border border-zinc-700 bg-zinc-900 text-zinc-400 uppercase font-bold">Desativada</span>}
            {m.advance_enabled && <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-700 bg-amber-950/40 text-amber-300 uppercase font-bold">Antecipa {m.advance_rate}%</span>}
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">Débito {m.debito}% (D+{m.settle_debito}) · Pix {m.pix}% (D+{m.settle_pix}) · Crédito D+{m.advance_enabled ? m.advance_settle : m.settle_credito} {m.split_credit_monthly ? "mês a mês" : "em um pagamento"}</div>
          <div className="text-[11px] text-zinc-500">{parcelas}</div>
        </div>
        <div className="flex items-center gap-1">
          {!m.is_default && m.active && <Button data-testid={`machine-default-${m.id}`} size="sm" variant="outline" disabled={busy} title="Definir como padrão" className="h-8 w-8 bg-zinc-900 border-zinc-800" onClick={() => act(api.post(`/shop-settings/payment-machines/${m.id}/default`))}><Star className="h-3.5 w-3.5" /></Button>}
          <Button data-testid={`machine-edit-${m.id}`} size="sm" variant="outline" disabled={busy} className="h-8 w-8 bg-zinc-900 border-zinc-800" onClick={() => onEdit(m)}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button data-testid={`machine-toggle-${m.id}`} size="sm" variant="outline" disabled={busy} title={m.active ? "Desativar" : "Ativar"} className="h-8 w-8 bg-zinc-900 border-zinc-800" onClick={() => act(api.patch(`/shop-settings/payment-machines/${m.id}/active`, { active: !m.active }))}><Power className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
    </li>
  );
}

function MonthReport() {
  const now = useMemo(() => new Date(), []);
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/erp/payment-machines/report", { params: { month } }).then(({ data }) => setData(data)).catch(() => setData({ rows: [], total: 0 })); }, [month]);
  return (
    <div data-testid="machines-report" className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-amber-brand" /><span className="font-display text-sm font-bold uppercase text-zinc-100">Taxas pagas no mês</span></div>
        <input data-testid="machines-report-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 bg-zinc-900 border border-zinc-800 rounded-md px-2 text-sm text-zinc-200" />
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-amber-brand" />
        : data.rows.length === 0 ? <p className="text-xs text-zinc-500">Nenhuma taxa de maquininha no mês.</p>
        : <ul className="space-y-1 text-sm">
            {data.rows.map((r) => (
              <li key={r.id || "_sem"} data-testid={`machines-report-row-${r.id || "sem"}`} className="flex items-center justify-between py-1 border-b border-zinc-800 last:border-0">
                <span className="text-zinc-200">{r.name}{r.removed ? <span className="ml-1 text-[10px] text-zinc-500">(desativada)</span> : null}</span>
                <span className="text-zinc-400 text-[11px]">{r.count} transações</span>
                <span className="font-display font-black text-red-300 tabular-nums">{brl(r.fees)}</span>
              </li>
            ))}
            <li className="flex items-center justify-between pt-2 text-sm font-bold">
              <span className="text-zinc-100">Total</span>
              <span data-testid="machines-report-total" className="font-display text-red-200 tabular-nums">{brl(data.total)}</span>
            </li>
          </ul>}
    </div>
  );
}

export function PaymentFeesCard() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/shop-settings/payment-machines").then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  if (list === null) return <Loader2 className="h-5 w-5 animate-spin text-green-brand" />;
  const onSaved = () => { setEditing(null); load(); };
  return (
    <section data-testid="payment-fees-card" className="rounded-2xl border border-card2-border bg-card2 p-5 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-sky-300" /><h2 className="font-display text-lg font-bold uppercase text-zinc-100">Maquininhas</h2><HelpTip screen="maquininhas" className="h-7 w-7" /></div>
        <Button data-testid="machine-add" size="sm" className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase" onClick={() => setEditing("new")}><Plus className="h-4 w-4 mr-1" /> Adicionar</Button>
      </div>
      <p className="text-xs text-zinc-500">Cada maquininha guarda suas taxas, prazo de recebimento e, se quiser, taxa de antecipação. A padrão já vem selecionada ao lançar o pagamento. Pagamentos antigos continuam como estão.</p>
      {editing === "new" && <MachineForm initial={null} onCancel={() => setEditing(null)} onSaved={onSaved} />}
      {list.length === 0 && editing !== "new" ? <p className="text-sm text-zinc-500">Nenhuma maquininha cadastrada ainda.</p> :
        <ul className="space-y-2">
          {list.map((m) => editing?.id === m.id
            ? <MachineForm key={m.id} initial={m} onCancel={() => setEditing(null)} onSaved={onSaved} />
            : <MachineRow key={m.id} m={m} onEdit={setEditing} onReload={load} />
          )}
        </ul>}
      <MonthReport />
    </section>
  );
}
